import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { fetchAuditLog, fetchTeamReport } from "@/api/reports";
import { Card, Skeleton } from "@/components";
import { useLang } from "@/i18n/LanguageContext";
import { actionLabel } from "@/features/reports/auditLabels";

/** At-a-glance KPIs for org-admins/managers, reusing the reporting service. */
export function DashboardOverview({ isOrgAdmin }: { isOrgAdmin: boolean }) {
  const { t } = useLang();
  const report = useQuery({ queryKey: ["team-report"], queryFn: ({ signal }) => fetchTeamReport(signal) });
  // Recent activity comes from the audit log, which only org-admins may read.
  const audit = useQuery({
    queryKey: ["audit-log", "recent"],
    queryFn: ({ signal }) => fetchAuditLog(6, signal),
    enabled: isOrgAdmin,
  });

  const totals = report.data?.totals;
  const openRequired = report.data?.members.reduce((sum, m) => sum + m.outstanding, 0) ?? 0;

  return (
    <div style={{ marginBottom: 28 }}>
      {report.isLoading ? (
        <Skeleton lines={3} />
      ) : totals && totals.people > 0 ? (
        <div className="stat-cards">
          <div className="stat-card"><div className="n">{totals.people}</div><div className="l">{t("Medewerkers", "People")}</div></div>
          <div className="stat-card"><div className="n">{totals.avg_percent}%</div><div className="l">{t("Gem. voortgang", "Avg. progress")}</div></div>
          <div className="stat-card"><div className="n">{totals.fully_done}</div><div className="l">{t("Volledig afgerond", "Fully completed")}</div></div>
          <div className="stat-card"><div className="n">{openRequired}</div><div className="l">{t("Open verplicht", "Required open")}</div></div>
        </div>
      ) : (
        <p className="no-data-message">{t("Nog geen medewerkers met een programma.", "No people with a program yet.")}</p>
      )}

      {totals && totals.people > 0 && (
        <p style={{ marginTop: 4 }}>
          <Link to="/app/reports" className="eyebrow">{t("Volledig teamoverzicht", "Full team overview")} →</Link>
        </p>
      )}

      {isOrgAdmin && audit.data && audit.data.length > 0 && (
        <Card style={{ marginTop: 18 }}>
          <p className="card-num">{t("RECENT", "RECENT")}</p>
          <h3 style={{ marginBottom: 10 }}>{t("Laatste activiteit", "Latest activity")}</h3>
          <ul className="list-rows">
            {audit.data.map((e) => (
              <li key={e.id}>
                <span>{actionLabel(e.action, t)}{e.actor_name ? ` · ${e.actor_name}` : ""}</span>
                <span className="ef-mono" style={{ color: "var(--grey-text)", fontSize: 12.5 }}>{new Date(e.created_at).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
